import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { InView } from "react-intersection-observer";
import PageVisibility from "react-page-visibility";
import loadSDK from "core/utils/loadSDK";
import {
  createSponsorClick,
  createSponsorImpression,
  trackAdPixel,
  postMessage,
} from "core/utils/tracker";
import { describeAdError } from "core/utils/describeAdError";
import {
  trackAdCreativeEvent,
  trackAdEvent,
  AD_LOAD_STARTED,
  AD_LOADED,
  AD_LOAD_FAILED,
} from "core/utils/adAnalytics";
import apm from "core/utils/apm";
import getAdUrl from "core/utils/getAdUrl";
import getReferer from "core/utils/getReferer";
import { buildAdConfig, getDefaultParams } from "core/utils/rsya";
import { usePlayerRsya } from "core/components/Player/PlayerRsyaContext";
import { usePlayerSuspended } from "core/components/Player/PlayerSuspendedContext";

const Root = styled("div")({
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  height: "100%",
  width: "100%",
  position: "absolute",
});

const Slot = styled("div")({
  position: "absolute",
  width: "100%",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  flex: 1,
  zIndex: 111000,
  pointerEvents: "none",
  backgroundColor: "black",
});

const StyledVideo = styled("video")({
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  height: "100%",
  width: "100%",
  position: "absolute",
});

const IMPRESSION = "IMPRESSION";
// const CREATIVEVIEW = "CREATIVEVIEW";
// const FULLSCREEN = "FULLSCREEN";
// const EXIT_FULLSCREEN = "EXIT_FULLSCREEN";
const ERROR = "ERROR";
const COMPLETE = "COMPLETE";
const CLICKTHROUGH = "CLICKTHROUGH";
const CLOSE = "CLOSE";
// const UNMUTE = "UNMUTE";
// const MUTE = "MUTE";
const SKIP = "SKIP";
const FIRSTQUARTILE = "FIRSTQUARTILE";
const MIDPOINT = "MIDPOINT";
const THIRDQUARTILE = "THIRDQUARTILE";
const NOBANNER_ERROR = "NOBANNER_ERROR";
const NETWORK_ERROR = "NETWORK_ERROR";
const NOT_FOUND = "NOT_FOUND";

// Сколько преролл ждёт видимости слота, прежде чем уступить место контенту.
const PREROLL_VISIBILITY_TIMEOUT = 5000;

const SDK_URL = "https://yandex.ru/ads/system/adsdk.js";

const creativeType = "Yandex SDK";

/* global ya */

const nobannerErrors = [
  "YANDEX_NO_AD_CUSTOM_MARKER",
  "NO_AD_SECTION",
  "VMAP_LOAD_TIMEOUT",
  "YANDEX_VAST_LOAD_TIMEOUT",
  "YANDEX_INVALID_VAST_XML",
];

export default function Yandex({
  vast,
  vastPlace,
  //   fullscreenTarget,
  onFinish,
  onStart,
  onError,
  muted,
  sportId,
  shareUrl,
}) {
  const slotRef = useRef();
  const videoRef = useRef();
  const emptyRef = useRef();
  const [adPlaybackController, setAdPlaybackController] = useState();
  const [adStore, setAdStore] = useState();
  const suspended = usePlayerSuspended();
  const resumeAfterSuspendRef = useRef(false);
  const rsya = usePlayerRsya();
  const rsyaRef = useRef(rsya);
  rsyaRef.current = rsya;

  const isPreroll = !!vastPlace && vastPlace.startsWith("PREROLL_");
  // undefined, пока IntersectionObserver не сообщил первое значение
  const [inView, setInView] = useState();
  const [tabVisible, setTabVisible] = useState(
    () =>
      typeof document === "undefined" || document.visibilityState !== "hidden"
  );
  const requestedRef = useRef(false);
  const adStartedRef = useRef(false);
  const impressionMarkedRef = useRef(false);

  // Флаг "идёт реклама" не должен залипнуть: снимаем, если сами ставили.
  const clearAdStarted = () => {
    if (!adStartedRef.current) return;

    adStartedRef.current = false;
    rsyaRef.current.setAdStarted(false);
  };

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const cancelledRef = useRef(false);
  const visibilityTimerRef = useRef();

  const stopVisibilityTimer = () => {
    clearTimeout(visibilityTimerRef.current);
    visibilityTimerRef.current = undefined;
  };

  // Преролл не ждёт видимости вечно: основное видео не должно зависнуть.
  useEffect(() => {
    if (!isPreroll) return;

    visibilityTimerRef.current = setTimeout(() => {
      visibilityTimerRef.current = undefined;
      if (requestedRef.current || cancelledRef.current) return;

      requestedRef.current = true;
      onErrorRef.current(new Error("RSYA_HIDDEN"));
    }, PREROLL_VISIBILITY_TIMEOUT);
  }, []);

  // Только на размонтирование; безопасно, потому что трогает одни ref'ы.
  useEffect(
    () => () => {
      cancelledRef.current = true;
      stopVisibilityTimer();
      clearAdStarted();
    },
    []
  );

  useEffect(() => {
    if (requestedRef.current) return;
    // Ждём первого сообщения о видимости слота.
    if (inView === undefined) return;

    const visible = inView && tabVisible;

    if (!visible) {
      // Преролл ждёт видимости, остальные места креатив пропускают.
      if (isPreroll) return;

      requestedRef.current = true;
      onError(new Error("RSYA_HIDDEN"));

      return;
    }

    stopVisibilityTimer();

    const { current } = emptyRef;
    const width = current?.offsetWidth;
    const height = current?.offsetHeight;

    const reason = rsyaRef.current.check({ width, height });

    if (reason) {
      requestedRef.current = true;
      onError(new Error("RSYA_" + reason));

      return;
    }

    requestedRef.current = true;

    trackAdEvent(AD_LOAD_STARTED, {
      silent: true,
      creative: "yandex",
      place: vastPlace,
    });

    loadSDK(SDK_URL, "yandex-ad-sdk")
      .then(() => !cancelledRef.current && ya.videoAd.loadModule("AdLoader"))
      .then((module) => {
        if (!module || cancelledRef.current) return null;

        // Пока грузился SDK, другой плеер мог сделать запрос.
        const lateReason = rsyaRef.current.check({ width, height });

        if (lateReason) {
          onError(new Error("RSYA_" + lateReason));

          return null;
        }

        const { content } = rsyaRef.current;

        const vastUrl = vast.url
          ? getAdUrl({
              url: vast.url,
              puid: vast.puid,
              sportId: sportId,
              shareUrl,
              width,
              height,
            })
          : undefined;

        const config = buildAdConfig({
          vast,
          defaults: getDefaultParams({
            nodeEnv: process.env.NODE_ENV,
            hostname: process.browser ? window.location.hostname : undefined,
          }),
          pageUrl: getReferer(),
          contentUrl: content.url,
          duration: content.duration,
          isLive: content.isLive,
          width,
          height,
          vastUrl,
        });

        rsyaRef.current.markRequest();

        return module.AdLoader.create(config);
      })
      .then((adLoader) => adLoader && adLoader.loadAd())
      .then((store) => {
        if (!store || cancelledRef.current) return;

        trackAdEvent(AD_LOADED, {
          silent: true,
          creative: "yandex",
          place: vastPlace,
        });

        return setAdStore(store);
      })
      .catch((error) => {
        if (cancelledRef.current) return;

        console.error(error);
        apm.captureError(error);

        trackAdEvent(AD_LOAD_FAILED, {
          silent: true,
          creative: "yandex",
          place: vastPlace,
          reason: error?.message,
        });

        if (
          error.message &&
          nobannerErrors.some((errorCode) => error.message.includes(errorCode))
        ) {
          createSponsorImpression(vast.token, creativeType, NOBANNER_ERROR, {
            error: describeAdError(error, {
              source: "yandex",
              code: "SDK_NO_BANNER",
            }),
          });
          trackAdCreativeEvent(NOBANNER_ERROR, {
            creative: "yandex",
            place: vastPlace,
          });
        } else {
          createSponsorImpression(vast.token, creativeType, NETWORK_ERROR, {
            error: describeAdError(error, {
              source: "yandex",
              code: "SDK_ERROR",
            }),
          });
          trackAdCreativeEvent(NETWORK_ERROR, {
            creative: "yandex",
            place: vastPlace,
          });
        }

        postMessage({
          eventName: NOT_FOUND,
          creative: "SponsorVideo",
          place: vastPlace,
        });

        clearAdStarted();
        onError(error);
      });
  }, [vast, inView, tabVisible]);

  useEffect(() => {
    if (!adStore) return;

    const videoSlot = videoRef.current;
    const slot = slotRef.current;

    const {
      token,
      clickPixel,
      additionalClickPixels,
      impressionPixel,
      additionalImpressionPixels,
    } = vast;

    const adPlaybackController = adStore.createPlaybackController(
      videoSlot,
      slot
    );

    adPlaybackController.subscribe("AdClickThru", function () {
      createSponsorClick(token);
      createSponsorImpression(token, creativeType, CLICKTHROUGH);
      trackAdCreativeEvent(CLICKTHROUGH, {
        creative: "yandex",
        place: vastPlace,
      });
      postMessage({
        eventName: CLICKTHROUGH,
        creative: "SponsorVideo",
        place: vastPlace,
      });

      trackAdPixel(clickPixel, additionalClickPixels);
    });

    adPlaybackController.subscribe("AdPodVideoFirstQuartile", function () {
      createSponsorImpression(token, creativeType, FIRSTQUARTILE);
      trackAdCreativeEvent(FIRSTQUARTILE, {
        creative: "yandex",
        place: vastPlace,
      });
      postMessage({
        eventName: FIRSTQUARTILE,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdPodVideoMidpoint", function () {
      createSponsorImpression(token, creativeType, MIDPOINT);
      trackAdCreativeEvent(MIDPOINT, { creative: "yandex", place: vastPlace });
      postMessage({
        eventName: MIDPOINT,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdPodVideoThirdQuartile", function () {
      createSponsorImpression(token, creativeType, THIRDQUARTILE);
      trackAdCreativeEvent(THIRDQUARTILE, {
        creative: "yandex",
        place: vastPlace,
      });
      postMessage({
        eventName: THIRDQUARTILE,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdPodComplete", function () {
      createSponsorImpression(token, creativeType, COMPLETE);
      trackAdCreativeEvent(COMPLETE, { creative: "yandex", place: vastPlace });
      postMessage({
        eventName: COMPLETE,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdPodSkipped", function () {
      createSponsorImpression(token, creativeType, SKIP);
      trackAdCreativeEvent(SKIP, { creative: "yandex", place: vastPlace });
      postMessage({
        eventName: SKIP,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdPodImpression", function () {
      createSponsorImpression(token, creativeType, IMPRESSION);
      trackAdCreativeEvent(IMPRESSION, {
        creative: "yandex",
        place: vastPlace,
      });
      postMessage({
        eventName: IMPRESSION,
        creative: "SponsorVideo",
        place: vastPlace,
      });
      trackAdPixel(impressionPixel, additionalImpressionPixels);

      if (!impressionMarkedRef.current) {
        impressionMarkedRef.current = true;
        rsyaRef.current.markImpression();
      }
    });

    adPlaybackController.subscribe("AdPodError", function (error) {
      console.log("AdPodError", error);
      createSponsorImpression(token, creativeType, ERROR);
      trackAdCreativeEvent(ERROR, { creative: "yandex", place: vastPlace });
      clearAdStarted();
      onError(error);
    });

    adPlaybackController.subscribe("AdPodClose", function () {
      createSponsorImpression(token, creativeType, CLOSE);
      trackAdCreativeEvent(CLOSE, { creative: "yandex", place: vastPlace });
      postMessage({
        eventName: CLOSE,
        creative: "SponsorVideo",
        place: vastPlace,
      });
    });

    adPlaybackController.subscribe("AdStarted", function () {
      // console.log("Ad started playing");
      adStartedRef.current = true;
      rsyaRef.current.setAdStarted(true);
      onStart();

      if (muted) {
        if (adPlaybackController.getAdVolume() !== 0) {
          adPlaybackController.setAdVolume(0);
        }
      }
    });

    adPlaybackController.subscribe("AdStopped", function () {
      // console.log("Ad stopped playing");
      clearAdStarted();
      onFinish();
      setAdStore();
    });

    // Запускаем проигрывание рекламы
    adPlaybackController.playAd();

    if (muted) {
      adPlaybackController.setAdVolume(0);
    }

    setAdPlaybackController(adPlaybackController);
  }, [adStore]);

  /**
   * Плеер под открытым поверх диалогом просит замолчать. Проверок ниже не
   * хватает: PageVisibility следит за вкладкой, а InView за попаданием в
   * область экрана, и под диалогом реклама остаётся и во вкладке, и в
   * области экрана, поэтому продолжала играть со звуком.
   *
   * Возобновляем только то, что сами и остановили: у ролика могла быть
   * своя пауза (ушёл из области экрана, свернули вкладку), и будить его
   * при закрытии диалога мы не должны.
   */
  useEffect(() => {
    if (!adPlaybackController) return;

    if (suspended) {
      resumeAfterSuspendRef.current = true;
      adPlaybackController.pauseAd();

      return;
    }

    if (!resumeAfterSuspendRef.current) return;

    resumeAfterSuspendRef.current = false;
    adPlaybackController.resumeAd();
  }, [suspended, adPlaybackController]);

  const onChangeVisibility = (isVisible) => {
    setTabVisible(isVisible);

    if (!adPlaybackController) return;
    // Под открытым диалогом реклама молчит, даже если вкладка активна.
    if (isVisible) {
      if (suspended) return;

      adPlaybackController.resumeAd();
    } else {
      adPlaybackController.pauseAd();
    }
  };

  const onChangeInView = (isInView) => {
    setInView(isInView);

    if (!adPlaybackController) return;
    // Под диалогом реклама остаётся в области экрана, но играть не должна.
    if (isInView) {
      if (suspended) return;

      adPlaybackController.resumeAd();
    } else {
      adPlaybackController.pauseAd();
    }
  };

  return (
    <PageVisibility onChange={onChangeVisibility}>
      <InView as={Root} threshold={0.5} onChange={onChangeInView}>
        {adStore ? (
          <Slot ref={slotRef}>
            <StyledVideo ref={videoRef} />
          </Slot>
        ) : (
          <div ref={emptyRef} style={{ width: "100%", height: "100%" }} />
        )}
      </InView>
    </PageVisibility>
  );
}

Yandex.propTypes = {
  onStart: PropTypes.func,
  onFinish: PropTypes.func,
  onUpdate: PropTypes.func,
  onChangeMute: PropTypes.func,
  onError: PropTypes.func,
  fullscreenTarget: PropTypes.string,
  vastPlace: PropTypes.string,
  muted: PropTypes.bool,
  vast: PropTypes.shape({
    url: PropTypes.string,
    token: PropTypes.string.isRequired,
    clickPixel: PropTypes.string,
    additionalClickPixels: PropTypes.arrayOf(PropTypes.string),
    impressionPixel: PropTypes.string,
    additionalImpressionPixels: PropTypes.arrayOf(PropTypes.string),
    puid: PropTypes.object,
  }).isRequired,
  sportId: PropTypes.string,
  shareUrl: PropTypes.string,
};
